import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { FaBell, FaPaperPlane, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';

import {
  deactivateMobileNotification,
  getMobileNotifications,
  searchMobileUsers,
  sendMobileNotification
} from '../../services/mobileNotificationService';
import './MobileNotifications.scss';

const initialForm = {
  title: '',
  message: '',
  type: 'service',
  targetType: 'all',
  targetUserId: '',
  actionRoute: '',
  scheduledAt: '',
  expiresAt: ''
};

const typeLabels = {
  service: 'Hizmet',
  event: 'Etkinlik',
  system: 'Sistem',
  warning: 'Uyarı',
  emergency: 'Acil'
};

const routeOptions = [
  ['', 'Yönlendirme yok'],
  ['/', 'Ana Sayfa'],
  ['/announcements', 'Duyurular'],
  ['/events', 'Etkinlikler'],
  ['/tenders', 'İhaleler'],
  ['/advertisements', 'İlanlar'],
  ['/projects', 'Projeler'],
  ['/map', 'Harita'],
  ['/report', 'Bize Bildir'],
  ['/randevu/spor', 'Spor Randevu'],
  ['/randevu/kutuphane', 'Kütüphane Randevu']
];

const toIsoOrNull = (value) => value ? new Date(value).toISOString() : null;

const MobileNotifications = () => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [notificationData, userData] = await Promise.all([
        getMobileNotifications(),
        searchMobileUsers()
      ]);
      setItems(Array.isArray(notificationData) ? notificationData : []);
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (err) {
      setError(err?.response?.data?.resultMessage || err.message || 'Bildirim verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (form.targetType !== 'user') return undefined;
    const timer = window.setTimeout(async () => {
      try {
        const data = await searchMobileUsers(userQuery);
        setUsers(Array.isArray(data) ? data : []);
      } catch {
        // Ana hata alanını geçmiş listesinin üzerine yazmamak için sessiz kalır.
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [form.targetType, userQuery]);

  const selectedUser = useMemo(
    () => users.find((user) => user.id === form.targetUserId),
    [users, form.targetUserId]
  );

  const submit = async (event) => {
    event.preventDefault();
    if (form.targetType === 'user' && !form.targetUserId) {
      toast.error('Hedef kullanıcı seçiniz.');
      return;
    }
    setSaving(true);
    try {
      const response = await sendMobileNotification({
        ...form,
        targetUserId: form.targetType === 'user' ? form.targetUserId : null,
        actionRoute: form.actionRoute || null,
        scheduledAt: toIsoOrNull(form.scheduledAt),
        expiresAt: toIsoOrNull(form.expiresAt)
      });
      if (response?.isSuccess === false) throw new Error(response.resultMessage);
      const recipientCount = response?.data?.recipientCount;
      toast.success(recipientCount ? `Bildirim ${recipientCount} kullanıcı için yayınlandı.` : 'Bildirim yayınlandı.');
      setForm(initialForm);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.resultMessage || err.message || 'Bildirim gönderilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (item) => {
    if (!window.confirm(`“${item.title}” bildirimini yayından kaldırmak istiyor musunuz?`)) return;
    try {
      const response = await deactivateMobileNotification(item.id);
      if (response?.isSuccess === false) throw new Error(response.resultMessage);
      toast.success('Bildirim yayından kaldırıldı.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.resultMessage || err.message || 'İşlem başarısız.');
    }
  };

  return (
    <div className="mobile-notifications-page">
      <div className="mobile-notifications-heading">
        <div>
          <h1><FaBell /> Mobil Bildirimler</h1>
          <p>Mobil uygulamadaki kullanıcılara genel veya kişiye özel bildirim yayınlayın.</p>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="notification-compose-card border-0 shadow-sm">
        <Card.Body>
          <h2>Yeni Bildirim</h2>
          <Form onSubmit={submit}>
            <Row className="g-3">
              <Col lg={8}>
                <Form.Group>
                  <Form.Label>Başlık *</Form.Label>
                  <Form.Control
                    value={form.title}
                    onChange={(event) => setForm({ ...form, title: event.target.value })}
                    maxLength={200}
                    required
                  />
                  <Form.Text>{form.title.length}/200</Form.Text>
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group>
                  <Form.Label>Bildirim Türü</Form.Label>
                  <Form.Select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>
                    {Object.entries(typeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Mesaj *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={form.message}
                    onChange={(event) => setForm({ ...form, message: event.target.value })}
                    maxLength={1500}
                    required
                  />
                  <Form.Text>{form.message.length}/1500</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Hedef</Form.Label>
                  <Form.Select
                    value={form.targetType}
                    onChange={(event) => setForm({ ...form, targetType: event.target.value, targetUserId: '' })}
                  >
                    <option value="all">Tüm aktif mobil kullanıcılar</option>
                    <option value="user">Tek kullanıcı</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Uygulama Yönlendirmesi</Form.Label>
                  <Form.Select value={form.actionRoute} onChange={(event) => setForm({ ...form, actionRoute: event.target.value })}>
                    {routeOptions.map(([value, label]) => <option key={value || 'none'} value={value}>{label}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              {form.targetType === 'user' && (
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Mobil Kullanıcı *</Form.Label>
                    <Form.Control
                      className="mb-2"
                      type="search"
                      value={userQuery}
                      onChange={(event) => setUserQuery(event.target.value)}
                      placeholder="Ad, e-posta veya telefon ile ara"
                    />
                    <Form.Select
                      value={form.targetUserId}
                      onChange={(event) => setForm({ ...form, targetUserId: event.target.value })}
                      required
                    >
                      <option value="">Kullanıcı seçiniz</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>{user.name} — {user.email}</option>
                      ))}
                    </Form.Select>
                    {selectedUser?.phone && <Form.Text>Telefon: {selectedUser.phone}</Form.Text>}
                  </Form.Group>
                </Col>
              )}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Yayın Zamanı</Form.Label>
                  <Form.Control type="datetime-local" value={form.scheduledAt} onChange={(event) => setForm({ ...form, scheduledAt: event.target.value })} />
                  <Form.Text>Boş bırakılırsa hemen yayınlanır.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Yayından Kalkma Zamanı</Form.Label>
                  <Form.Control type="datetime-local" value={form.expiresAt} onChange={(event) => setForm({ ...form, expiresAt: event.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={12} className="text-end">
                <Button type="submit" disabled={saving}>
                  {saving ? <Spinner size="sm" className="me-2" /> : <FaPaperPlane className="me-2" />}
                  {form.scheduledAt ? 'Bildirimi Planla' : 'Bildirimi Gönder'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card className="notification-history-card border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="history-title"><h2>Gönderim Geçmişi</h2><span>Son 100 kayıt</span></div>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead><tr><th>Bildirim</th><th>Hedef</th><th>Yayın</th><th>Okunma</th><th>Push</th><th>Durum</th><th /></tr></thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td><strong>{item.title}</strong><small>{item.message}</small><Badge bg="info">{typeLabels[item.type] || item.type}</Badge></td>
                      <td>{item.targetType === 'all' ? 'Tüm kullanıcılar' : item.targetUserName || 'Tek kullanıcı'}</td>
                      <td>{new Date(item.scheduledAt || item.createdAt).toLocaleString('tr-TR')}</td>
                      <td>{item.readCount}</td>
                      <td>
                        {item.pushSentAt
                          ? <Badge bg="success">Cihaza gönderildi</Badge>
                          : item.pushError
                            ? <Badge bg="danger" title={item.pushError}>Gönderilemedi</Badge>
                            : <Badge bg="warning" text="dark">Bekliyor</Badge>}
                      </td>
                      <td><Badge bg={item.isActive ? 'success' : 'secondary'}>{item.isActive ? 'Yayında' : 'Kaldırıldı'}</Badge></td>
                      <td className="text-end">
                        {item.isActive && <Button variant="outline-danger" size="sm" onClick={() => deactivate(item)} title="Yayından kaldır"><FaTrash /></Button>}
                      </td>
                    </tr>
                  ))}
                  {!items.length && <tr><td colSpan="7" className="text-center text-muted py-5">Henüz mobil bildirim gönderilmedi.</td></tr>}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default MobileNotifications;
